import './App.css';
import './BentoHome.css';
import BentoHome from './BentoHome';
import { useEffect, useRef, useState } from 'react';
import { useSound } from 'react-sounds';
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Cloud,
  Database,
  ExternalLink,
  Server,
  Smartphone,
  Volume2,
  VolumeX,
  Wrench,
  X
} from 'lucide-react';

const featuredProjects = [
  {
    tag: 'Capstone project',
    title: 'USTCB DigiLib',
    role: 'Project Manager, QA Support, and Test Contributor',
    desc: 'A digital library capstone for UST Communications Bureau with resource management, borrowing workflows, and media-based learning resources.',
    long: 'USTCB DigiLib is a digital library capstone project developed for the UST Communications Bureau to streamline library access, resource management, borrowing workflows, user administration, and video/audio-based learning resources through a web-based platform. The system supports different user roles and helps make library services more organized, accessible, and easier to manage. I led project planning, task coordination, and team communication for a 4-person capstone team composed of 2 developers, 1 QA analyst, and myself. I regularly coordinated with the client to clarify requirements, gather feedback, and align development with project expectations. I also contributed heavily to quality assurance by assisting with manual testing, creating/running automated tests, validating features, reporting issues, and helping ensure the system worked reliably before delivery.',
    features: ['Library access workflows', 'Resource management', 'Borrowing workflows', 'User administration', 'Video and audio learning resources', 'Client requirements coordination', 'Manual and automated testing support', 'Issue reporting and feature validation'],
    tags: ['React.js', 'JavaScript', 'CSS', 'Node.js', 'Express', 'Oracle Database', 'Docker', 'OpenAI Whisper', 'FFmpeg', 'GitHub'],
    stack: 'cloud',
    deployed: false,
    status: 'Deployment in progress',
    image: '/images/cbdigilib/cbdigilib.png',
    images: [
      '/images/cbdigilib/cbdigilib.png',
      '/images/cbdigilib/cbdigilib1.png',
      '/images/cbdigilib/cbdigilib2.png',
      '/images/cbdigilib/cbdigilib3.png'
    ],
    demo: 'https://github.com/Juman-Shinobi',
    code: '',
    codeStatus: 'private'
  },
  {
    tag: 'Flask web application',
    title: 'The Three Musketeers IP Lookup',
    role: 'Backend developer',
    desc: 'A Flask dashboard that displays public IPv4 and IPv6 network details through external IP lookup APIs.',
    long: 'The Three Musketeers IP Lookup is a Flask-based web application that displays a user\'s public IPv4 and IPv6 information in a clean dashboard interface. It connects to external IP lookup APIs to retrieve network details such as IP address, location, timezone, organization, and ASN, then presents the results in responsive profile cards. The project demonstrates backend API integration, Flask routing, Jinja templating, error handling for failed requests, and frontend styling with responsive HTML and CSS.',
    features: ['IPv4 and IPv6 lookup', 'External API integration', 'Location and ASN details', 'Responsive dashboard cards', 'Request error handling'],
    tags: ['Python', 'Flask', 'Jinja2', 'Requests', 'HTML5', 'CSS3', 'ipify', 'ipapi.co'],
    stack: 'backend',
    deployed: false,
    status: 'Not deployed',
    image: '/images/ThreeMusketeers/Public IP.png',
    images: ['/images/ThreeMusketeers/Public IP.png'],
    demo: 'https://github.com/Juman-Shinobi',
    code: '',
    codeStatus: 'pending'
  },
  {
    tag: 'Clinic management system',
    title: 'DentaSafe',
    role: 'Primary front-end developer / assistant back-end developer',
    desc: 'A dental clinic management system for patient records, appointments, treatments, user roles, and activity logs.',
    long: 'DentaSafe is a web-based dental clinic record management system designed to help clinics manage patient records, dentist accounts, appointments, treatment records, users, and activity logs in one centralized platform. The system supports multiple user roles, including Admin, Dentist, Employee, and Client, each with different access privileges to improve security and workflow control. Clients can register and book appointments, while admins can manage users, patients, dentists, appointments, treatments, and monitor system activity through logs. My main role in the project was Primary Front-End Developer, while also assisting in back-end development.',
    features: ['Patient record management', 'Dentist account management', 'Appointment scheduling', 'Treatment records', 'Role-based access control', 'Activity logs', 'Search functionality', 'Login and logout sessions'],
    tags: ['PHP', 'MySQL', 'HTML', 'CSS', 'Bootstrap', 'JavaScript', 'SweetAlert2', 'jQuery'],
    stack: 'backend',
    deployed: false,
    status: 'Not deployed',
    image: '/images/dentasafe/dentasafe.png',
    images: [
      '/images/dentasafe/dentasafe.png',
      '/images/dentasafe/dentasafe2.png',
      '/images/dentasafe/dentasafe3.png',
      '/images/dentasafe/dentasafe4.png',
      '/images/dentasafe/dentasafe5.png',
      '/images/dentasafe/dentasafe6.png'
    ],
    demo: 'https://github.com/Juman-Shinobi',
    code: '',
    codeStatus: 'pending'
  },
  {
    tag: 'Foundation portal',
    title: 'Bayanihan Portal',
    role: 'Quality Assurance Analyst',
    desc: 'A Tanging Yaman Foundation portal for content management, employee requests, beneficiary reports, and feedback.',
    long: 'Bayanihan Portal is a web-based platform developed for Tanging Yaman Foundation Inc. to modernize its existing website and support both public engagement and internal administrative processes. The system was designed to help the organization manage website content, employee requests, beneficiary reports, user feedback, and partner submissions in a more organized and accessible way. It includes role-based access for Admin, Employee, Partner, and Guest users, allowing each user type to access only the features relevant to their responsibilities. My role in the project was Quality Assurance Officer, where I helped review system requirements, validate features, and ensure that the system aligned with the client\'s needs.',
    features: ['Role-based user access', 'Content management and approval', 'Employee request management', 'Beneficiary report submission', 'User experience feedback', 'Admin, employee, partner, and guest modules', 'PDF and CSV report generation'],
    tags: ['Laravel', 'PHP', 'MySQL', 'Blade', 'Tailwind CSS', 'Alpine.js', 'FullCalendar', 'Dompdf'],
    stack: 'backend',
    deployed: false,
    status: 'Not deployed',
    image: '/images/bayanihan/tangingyaman.png',
    images: [
      '/images/bayanihan/tangingyaman.png',
      '/images/bayanihan/tangingyaman2.png',
      '/images/bayanihan/tangingyaman4.png',
      '/images/bayanihan/tangingyaman5.png'
    ],
    demo: 'https://tanging-yaman.org',
    code: '',
    codeStatus: 'private'
  }
];

const allProjects = [
  featuredProjects[0],
  featuredProjects[3],
  {
    tag: 'Mobile school project',
    title: 'FoodMo!',
    role: 'Mobile application developer',
    desc: 'A mobile restaurant discovery app for browsing food places, filtering cuisines, viewing ratings, and checking restaurant details.',
    long: 'FoodMo! is a school mobile application project focused on helping users discover restaurants through a clean mobile interface. The app presents restaurant cards with cuisine categories, ratings, location details, verified reviews, operating hours, and search and filter controls so users can browse food options more easily.',
    features: ['Restaurant discovery', 'Cuisine category filters', 'Restaurant search', 'Rating-based filtering', 'Restaurant profile cards', 'Location details', 'Verified reviews', 'Operating hours'],
    tags: ['Flutter', 'Dart', 'Firebase', 'Mobile UI', 'Authentication'],
    stack: 'mobile',
    deployed: false,
    status: 'School project',
    image: '/images/FoodMo/foodmo1.png',
    images: [
      '/images/FoodMo/foodmo1.png',
      '/images/FoodMo/foodmo2.png',
      '/images/FoodMo/foodmo3.png',
      '/images/FoodMo/foodmo4.png'
    ],
    demo: 'https://github.com/Juman-Shinobi',
    code: '',
    codeStatus: 'pending'
  },
  featuredProjects[1],
  featuredProjects[2],
  {
    tag: 'Movie reservation app',
    title: 'MoviesTayo!',
    role: 'Full Stack Developer',
    desc: 'A movie reservation web app for browsing movies, viewing showtimes, selecting seats, and managing reservations.',
    long: 'MoviesTayo! is a movie reservation web application that lets users browse movies, view theaters and showtimes, select seats, and manage reservations. It also includes an admin dashboard for managing movies, theaters, showtimes, seats, bookings, user accounts, and activity logs.',
    features: ['Movie browsing', 'Theater and showtime viewing', 'Seat selection', 'Reservation management', 'Admin dashboard', 'Movie and theater management', 'Booking management', 'User accounts and activity logs'],
    tags: ['Laravel 12', 'PHP 8.2', 'Blade', 'Tailwind CSS', 'Alpine.js', 'Vite', 'SQLite', 'Laravel Breeze'],
    stack: 'backend',
    deployed: true,
    image: '/images/MoviesTayo/moviestayo2.png',
    images: [
      '/images/MoviesTayo/moviestayo1.png',
      '/images/MoviesTayo/moviestayo2.png',
      '/images/MoviesTayo/moviestayo3.png',
      '/images/MoviesTayo/moviestayo4.png',
      '/images/MoviesTayo/moviestayo5.png'
    ],
    demo: 'http://moviestayo.ct.ws',
    code: 'https://github.com/Juman-Shinobi/MoviesTAYO',
    codeStatus: 'public'
  },
  {
    tag: 'Cloud infrastructure',
    title: 'Portfolio Website',
    role: 'Cloud engineer',
    desc: 'Personal portfolio prepared for AWS hosting with a static React build and deployment workflow.',
    long: 'Personal portfolio prepared for AWS hosting with a static React build and a deployment workflow. The project is focused on presenting portfolio work clearly while keeping the structure ready for cloud hosting.',
    features: ['React portfolio UI', 'Static production build', 'AWS-ready deployment structure'],
    tags: ['React.js', 'AWS', 'S3', 'CI/CD'],
    stack: 'cloud',
    deployed: true,
    image: '/images/aws.png',
    images: ['/images/aws.png', '/images/cbdigilib/cbdigilib.png', '/images/bayanihan/tangingyaman.png'],
    demo: '/',
    code: '',
    codeStatus: 'pending'
  }
];

const homepageProjects = [featuredProjects[0], featuredProjects[3]];

const filters = [
  { key: 'all', label: 'All' },
  { key: 'frontend', label: 'Frontend' },
  { key: 'backend', label: 'Backend' },
  { key: 'mobile', label: 'Mobile' },
  { key: 'cloud', label: 'Cloud' }
];

const stackGroups = [
  {
    title: 'Frontend',
    icon: Server,
    items: ['HTML', 'CSS', 'JavaScript', 'React.js', 'Tailwind CSS', 'Bootstrap']
  },
  {
    title: 'Backend & data',
    icon: Database,
    items: ['PHP', 'Laravel', 'Node.js', 'Express', 'REST APIs', 'MySQL', 'Oracle Database', 'Firebase']
  },
  {
    title: 'Mobile',
    icon: Smartphone,
    items: ['Flutter', 'Dart']
  },
  {
    title: 'Cloud & DevOps',
    icon: Cloud,
    items: ['AWS', 'S3', 'CloudFront', 'ACM', 'Cloudflare', 'Docker']
  },
  {
    title: 'Tools',
    icon: Wrench,
    items: ['Git', 'GitHub', 'VS Code', 'Postman', 'XAMPP', 'phpMyAdmin']
  }
];

function Slideshow({ images, title }) {
  const [slide, setSlide] = useState(0);

  const goToSlide = (direction) => {
    setSlide((current) => (current + direction + images.length) % images.length);
  };

  return (
    <div className="ap-slideshow">
      <img src={images[slide]} alt={`${title} screenshot ${slide + 1}`} />
      {images.length > 1 && (
        <>
          <button
            className="ap-slide-nav prev"
            onClick={(event) => {
              event.stopPropagation();
              goToSlide(-1);
            }}
            type="button"
            aria-label="Previous image"
          >
            <ChevronLeft />
          </button>
          <button
            className="ap-slide-nav next"
            onClick={(event) => {
              event.stopPropagation();
              goToSlide(1);
            }}
            type="button"
            aria-label="Next image"
          >
            <ChevronRight />
          </button>
          <div className="ap-slide-dots" aria-hidden="true">
            {images.map((image, index) => (
              <span className={`ap-slide-dot ${index === slide ? 'active' : ''}`} key={`${image}-${index}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function App() {
  const [page, setPage] = useState(() => (window.location.pathname === '/projects' ? 'projects' : 'home'));
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);
  const [codeNotice, setCodeNotice] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const lastHoverSoundAt = useRef(0);
  const { play: playInputFocus } = useSound('ui/radio_select', { volume: 0.70 });
  const { play: playPopOpen } = useSound('ui/item_deselect', { volume: 0.70 });

  useEffect(() => {
    const handlePopState = () => {
      setPage(window.location.pathname === '/projects' ? 'projects' : 'home');
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setSelectedProject(null);
        setCodeNotice(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const navigateTo = (event, nextPage) => {
    event.preventDefault();
    const nextPath = nextPage === 'projects' ? '/projects' : '/';
    window.history.pushState({}, '', nextPath);
    setPage(nextPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const playHoverSound = () => {
    const now = Date.now();

    if (!soundEnabled) {
      return;
    }

    if (now - lastHoverSoundAt.current < 120) {
      return;
    }

    lastHoverSoundAt.current = now;
    playInputFocus().catch(() => {});
  };

  const playNoticeSound = () => {
    if (!soundEnabled) {
      return;
    }

    playPopOpen().catch(() => {});
  };

  const shownProjects =
    activeFilter === 'all' ? allProjects : allProjects.filter((project) => project.stack === activeFilter);

  const getCodeNoticeMessage = (project) => {
    if (project.codeNotice) {
      return project.codeNotice;
    }

    if (project.codeStatus === 'private') {
      return `${project.title}'s source code is currently private for security and client confidentiality reasons.`;
    }

    return `${project.title}'s public repository is still being prepared for viewing. Please check back soon.`;
  };

  const handleCodeClick = (event, project) => {
    event.preventDefault();
    event.stopPropagation();

    if (project.codeStatus === 'public' && project.code) {
      window.open(project.code, '_blank', 'noopener,noreferrer');
      return;
    }

    playNoticeSound();
    setCodeNotice({
      title: project.title,
      message: getCodeNoticeMessage(project)
    });
  };

  const soundToggle = (
    <button
      className={`sound-toggle ${soundEnabled ? 'active' : ''}`}
      onClick={() => setSoundEnabled((enabled) => !enabled)}
      type="button"
      aria-label={soundEnabled ? 'Mute sounds' : 'Turn sounds on'}
      aria-pressed={soundEnabled}
      title={soundEnabled ? 'Mute sounds' : 'Turn sounds on'}
    >
      {soundEnabled ? <Volume2 /> : <VolumeX />}
      <span>{soundEnabled ? 'On' : 'Mute'}</span>
    </button>
  );

  if (page === 'projects') {
    return (
      <main className="ap-root">
        <div className="ap-wrap">
          <a className="ap-back" href="/" onClick={(event) => navigateTo(event, 'home')}>
            <ArrowLeft /> Back to profile
          </a>

          <header className="ap-head">
            <h1>All projects</h1>
            <p>
              A closer look at what I&apos;ve built across coursework, org work, and things I made just to learn
              something new.
            </p>
          </header>

          <div className="ap-filters" aria-label="Project filters">
            {filters.map((filter) => (
              <button
                className={`ap-filter ${activeFilter === filter.key ? 'active' : ''}`}
                key={filter.key}
                onClick={() => setActiveFilter(filter.key)}
                type="button"
              >
                {filter.label}
              </button>
            ))}
          </div>

          <section className="ap-grid">
            {shownProjects.map((project) => (
              <article
                className="ap-card"
                key={project.title}
                onMouseEnter={playHoverSound}
                onClick={() => setSelectedProject(project)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setSelectedProject(project);
                  }
                }}
                role="button"
                tabIndex="0"
              >
                <div className="ap-thumb">
                  <img src={project.image} alt={`${project.title} screenshot`} />
                  {!project.deployed && <span className="ap-thumb-badge">{project.status || 'Not deployed'}</span>}
                </div>
                <div className="ap-card-body">
                  <div className="ap-card-top">
                    <span className="ap-pill">{project.tag}</span>
                    <div className="ap-links" onClick={(event) => event.stopPropagation()}>
                      <button type="button" onClick={(event) => handleCodeClick(event, project)} aria-label={`${project.title} source code`}>
                        <span className="github-mark">GH</span>
                      </button>
                      {project.deployed && (
                        <a href={project.demo} target={project.demo === '/' ? '_self' : '_blank'} rel="noreferrer" aria-label={`${project.title} live project`}>
                          <ExternalLink />
                        </a>
                      )}
                    </div>
                  </div>
                  <h2>{project.title}</h2>
                  <p className="ap-role">{project.role}</p>
                  <p className="ap-desc">{project.desc}</p>
                  <div className="ap-tags">
                    {project.tags.map((tag) => (
                      <span className="ap-tag" key={tag}>{tag}</span>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </section>
        </div>

        {selectedProject && (
          <div className="ap-overlay" onClick={() => setSelectedProject(null)}>
            <section className="ap-modal" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="project-modal-title">
              <button className="ap-modal-close" onClick={() => setSelectedProject(null)} type="button" aria-label="Close project preview">
                <X />
              </button>
              <Slideshow images={selectedProject.images} title={selectedProject.title} />
              <div className="ap-modal-body">
                <div className="ap-modal-top">
                  <span className="ap-pill">{selectedProject.tag}</span>
                </div>
                <h2 id="project-modal-title">{selectedProject.title}</h2>
                <p className="ap-modal-role">{selectedProject.role}</p>
                <p className="ap-modal-long">{selectedProject.long}</p>
                {selectedProject.features && (
                  <div className="ap-feature-block">
                    <h3>Key features</h3>
                    <ul>
                      {selectedProject.features.map((feature) => (
                        <li key={feature}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="ap-tags">
                  {selectedProject.tags.map((tag) => (
                    <span className="ap-tag" key={tag}>{tag}</span>
                  ))}
                </div>
                <div className="ap-modal-actions">
                  <button className="ap-modal-btn primary" onClick={(event) => handleCodeClick(event, selectedProject)} type="button">
                    <span className="github-mark">GH</span> View code
                  </button>
                  {selectedProject.deployed && (
                    <a className="ap-modal-btn ghost" href={selectedProject.demo} target={selectedProject.demo === '/' ? '_self' : '_blank'} rel="noreferrer">
                      <ExternalLink /> Live site
                    </a>
                  )}
                </div>
              </div>
            </section>
          </div>
        )}
        {codeNotice && (
          <div className="code-notice-overlay" onClick={() => setCodeNotice(null)}>
            <section className="code-notice" onClick={(event) => event.stopPropagation()} role="alertdialog" aria-modal="true" aria-labelledby="code-notice-title">
              <button className="code-notice-close" onClick={() => setCodeNotice(null)} type="button" aria-label="Close source code notice">
                <X />
              </button>
              <span className="code-notice-badge">Source code unavailable</span>
              <h2 id="code-notice-title">{codeNotice.title}</h2>
              <p>{codeNotice.message}</p>
              <button className="code-notice-action" onClick={() => setCodeNotice(null)} type="button">
                Got it
              </button>
            </section>
          </div>
        )}
        {soundToggle}
      </main>
    );
  }

  return <BentoHome projects={homepageProjects} stackGroups={stackGroups} navigateTo={navigateTo} playHoverSound={playHoverSound} soundToggle={soundToggle} />;
}

export default App;
